"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense } from "react";
import {
  MY_PAGE_NAV,
  MY_PAGE_SUPPORT_LINKS,
  isMyPageActivityPath,
} from "@/lib/config/myPageNav";

function isActive(pathname: string, href: string) {
  if (href === "/profile") return pathname === "/profile";
  if (href === "/profile/activity") return isMyPageActivityPath(pathname);
  const pathOnly = href.split("?")[0];
  return pathname === pathOnly || pathname.startsWith(`${pathOnly}/`);
}

function isSupportActive(
  pathname: string,
  search: string,
  href: string
) {
  const [pathOnly, query = ""] = href.split("?");
  if (pathname !== pathOnly && !pathname.startsWith(`${pathOnly}/`)) {
    return false;
  }
  const wantCompose = query.includes("tab=compose");
  const hasCompose = new URLSearchParams(search).get("tab") === "compose";
  return wantCompose ? hasCompose : !hasCompose;
}

function MyPageShellInner({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() || "/profile";
  const searchParams = useSearchParams();
  const search = searchParams?.toString() || "";
  const flatItems = MY_PAGE_NAV.flatMap(section => section.items);

  return (
    <div className="min-h-screen bg-[#f5f6f8]">
      <div className="mx-auto max-w-5xl px-3 py-4 sm:px-5 sm:py-8">
        <div className="mb-4 md:hidden">
          <div className="flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none]">
            {flatItems.map(item => {
              const active = isActive(pathname, item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                    active
                      ? "bg-gray-900 text-white"
                      : "bg-white text-gray-600 ring-1 ring-gray-200"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            {MY_PAGE_SUPPORT_LINKS.map(item => {
              const active = isSupportActive(pathname, search, item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                    active
                      ? "bg-gray-900 text-white"
                      : "bg-white text-gray-600 ring-1 ring-gray-200"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>

        <div className="flex gap-6 lg:gap-8">
          <aside className="hidden w-52 shrink-0 md:block lg:w-56">
            <div className="sticky top-20 overflow-hidden rounded-2xl border border-gray-200 bg-white">
              {MY_PAGE_NAV.map((section, sectionIndex) => (
                <div
                  key={section.id}
                  className={sectionIndex > 0 ? "border-t border-gray-100" : ""}
                >
                  <p className="px-4 pb-1 pt-3 text-[11px] font-semibold text-gray-400">
                    {section.title}
                  </p>
                  <nav className="pb-2">
                    {section.items.map(item => {
                      const active = isActive(pathname, item.href);
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          className={`block px-4 py-2 text-sm transition ${
                            active
                              ? "bg-gray-50 font-semibold text-gray-950"
                              : "font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                          }`}
                        >
                          {item.label}
                        </Link>
                      );
                    })}
                  </nav>
                </div>
              ))}

              <div className="border-t border-gray-100 pb-2 pt-3">
                <p className="px-4 pb-1 text-[11px] font-semibold text-gray-400">
                  고객지원
                </p>
                <nav>
                  {MY_PAGE_SUPPORT_LINKS.map(item => {
                    const active = isSupportActive(pathname, search, item.href);
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={`block px-4 py-2 text-sm transition ${
                          active
                            ? "bg-gray-50 font-semibold text-gray-950"
                            : "font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </div>
          </aside>

          <main className="min-w-0 flex-1">{children}</main>
        </div>
      </div>
    </div>
  );
}

export function MyPageShell({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#f5f6f8]" />}>
      <MyPageShellInner>{children}</MyPageShellInner>
    </Suspense>
  );
}
